import React, { useState, useEffect } from 'react';
import { auditApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react';

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 30;
  const pages = Math.ceil(total / limit);

  useEffect(() => {
    setLoading(true);
    auditApi.list({ page, limit })
      .then(({ data }) => { setLogs(data.logs); setTotal(data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Audit Log</h1>
        <p className="text-sm text-muted-foreground mt-1">Track all changes made to entries</p>
      </div>

      <div className="bg-card border border-border overflow-x-auto" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Timestamp</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Action</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">User</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Entry ID</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Changes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No audit logs yet
              </td></tr>
            ) : logs.map(log => (
              <tr key={log._id} className="border-b border-border hover:bg-muted/50">
                <td className="py-3 px-4 whitespace-nowrap text-xs">{new Date(log.timestamp).toLocaleString()}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-medium px-2 py-0.5 uppercase tracking-wider ${
                    log.action === 'create' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400' :
                    log.action === 'delete' ? 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400' :
                    'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400'
                  }`}>
                    {log.action}
                  </span>
                </td>
                <td className="py-3 px-4">{log.user_name || log.user_id}</td>
                <td className="py-3 px-4 font-mono text-xs">{log.entry_id?.slice(-8)}</td>
                <td className="py-3 px-4 text-xs max-w-[300px]">
                  {log.action === 'update' ? (
                    <div className="space-y-0.5">
                      {Object.entries(log.changes || {}).map(([key, val]) => (
                        <div key={key}>
                          <span className="font-medium">{key}</span>: <span className="text-red-600 line-through">{String(val.old)}</span> → <span className="text-emerald-600">{String(val.new)}</span>
                        </div>
                      ))}
                    </div>
                  ) : log.action === 'create' ? (
                    <span className="text-muted-foreground">New entry created</span>
                  ) : (
                    <span className="text-muted-foreground">Entry deleted</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
